/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { minicartStyles } from "@eisinc/App/Components/XngMiniCart/XngMiniCartStyles";
import Button from "@eisinc/MobiusOverrides/Button";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canCheckoutWithCart,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import Link from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        cart,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        isCartCheckoutDisabled: isCartCheckoutDisabled(cart),
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        isPunchOutOrder: isPunchOutOrder(cart),
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
};

interface Props {
    cutsDoesNotEqualQty: boolean;
}

type OwnProps = Props & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const MiniCartActionButtons: React.FC<OwnProps> = ({
    cart,
    cartPageUrl,
    canCheckoutWithCart,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
    checkoutShippingPageUrl,
    isPunchOutOrder,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    history,
    canSubmitRequisition,
    submitRequisition,
    requisitionConfirmationPageUrl,
    cutsDoesNotEqualQty,
}) => {
    const isDisabled =
        hasOnlyQuoteRequiredProducts || isCartCheckoutDisabled || cart?.cartNotPriced || cutsDoesNotEqualQty;

    const checkoutHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        preloadCheckoutShippingData({
            onSuccess: () => {
                history.push(checkoutShippingPageUrl!);
            },
        });
    };

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    return (
        <StyledWrapper {...minicartStyles.minicartActionButtonsStyles}>
            <Link href={cartPageUrl}>
                <Button variant="secondary">{translate("View Cart")}</Button>
            </Link>
            <StyledWrapper {...minicartStyles.minicartCheckoutButton}>
                {!canSubmitRequisition ? (
                    <Button onClick={checkoutHandler} variant="primary" disabled={isDisabled}>
                        {cart?.requiresApproval && !canSubmitRequisition
                            ? translate("Checkout for Approval")
                            : isPunchOutOrder
                              ? translate("Return to Procurement System")
                              : translate("Checkout")}
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        disabled={cart?.cartNotPriced}
                        onClick={submitRequisitionClickHandler}
                        data-test-selector="cartTotal_SubmitRequisition"
                    >
                        {translate("Submit Requisition")}
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCartActionButtons));
